import { useState, FormEvent } from 'react'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Textarea } from '../../../components/ui/textarea'
import { Button } from '../../../components/ui/button'
import { Label } from '../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import { ProjectSearchPopover } from './ProjectSearchPopover'
import { IconPicker } from './IconPicker'

interface AdminTemplateNewProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function New(_props: AdminTemplateNewProps) {
  const { t } = useTranslation('admin')
  const [formData, setFormData] = useState({
    key: '',
    name: '',
    description: '',
    category: 'business',
    icon: 'file-plus',
    source_repo: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    router.post('/admin/templates', {
      template: formData,
    }, {
      onFinish: () => setIsSubmitting(false),
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: t('templates.title'), href: '/admin/templates' },
        { label: t('templates.new.title') },
      ]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              <div className="max-w-2xl mx-auto">
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-foreground">{t('templates.new.title')}</h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('templates.new.description')}
                  </p>
                </div>

                <Card className="shadow-none">
                  <CardHeader>
                    <CardTitle>{t('templates.new.details')}</CardTitle>
                    <CardDescription>{t('templates.new.detailsDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="key">{t('templates.form.key')}</Label>
                        <Input
                          id="key"
                          type="text"
                          value={formData.key}
                          onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                          required
                        />
                        <p className="text-xs text-muted-foreground">{t('templates.form.keyHelp')}</p>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="name">{t('templates.form.name')}</Label>
                        <Input
                          id="name"
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="description">{t('templates.form.description')}</Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('templates.form.category')}</Label>
                          <Select
                            value={formData.category}
                            onValueChange={(value) => setFormData({ ...formData, category: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="starter">{t('templates.categories.starter')}</SelectItem>
                              <SelectItem value="business">{t('templates.categories.business')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>{t('templates.form.icon')}</Label>
                          <IconPicker
                            value={formData.icon}
                            onValueChange={(value) => setFormData({ ...formData, icon: value })}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="source_repo">{t('templates.form.sourceRepo')}</Label>
                        <div className="flex gap-2">
                          <Input
                            id="source_repo"
                            type="text"
                            value={formData.source_repo}
                            onChange={(e) => setFormData({ ...formData, source_repo: e.target.value })}
                            required
                          />
                          <ProjectSearchPopover
                            onSelect={(name) => setFormData({ ...formData, source_repo: name })}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">{t('templates.form.sourceRepoHelp')}</p>
                      </div>

                      <div className="flex justify-end gap-3 pt-6">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => router.visit('/admin/templates')}
                          disabled={isSubmitting}
                        >
                          {t('templates.form.cancel')}
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                          {isSubmitting ? t('templates.form.creating') : t('templates.form.create')}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

New.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default New
